import { useState, useRef, useEffect, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, EnvelopeSimple, ArrowClockwise, CheckCircle } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { authApi } from "@/lib/apiClient";
import { PassportLogo } from "@/components/Reveal";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const OTP_LENGTH = 6;
const RESEND_COOLDOWN = 30; // seconds before resend button unlocks
const EXPIRY_MINUTES = 15;

export default function VerifyEmail() {
    const [sp] = useSearchParams();
    const email = sp.get("email") || "";
    const nav = useNavigate();
    const { verifyEmail } = useAuth();
    const { t } = useTranslation();

    const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(""));
    const [loading, setLoading] = useState(false);
    const [resending, setResending] = useState(false);
    const [cooldown, setCooldown] = useState(RESEND_COOLDOWN);
    const [timeLeft, setTimeLeft] = useState(EXPIRY_MINUTES * 60); // seconds
    const [error, setError] = useState("");
    const inputRefs = useRef([]);

    // Expiry countdown
    useEffect(() => {
        if (timeLeft <= 0) return;
        const timer = setInterval(() => setTimeLeft((s) => s - 1), 1000);
        return () => clearInterval(timer);
    }, [timeLeft]);

    // Resend cooldown countdown
    useEffect(() => {
        if (cooldown <= 0) return;
        const timer = setInterval(() => setCooldown((s) => s - 1), 1000);
        return () => clearInterval(timer);
    }, [cooldown]);

    const formatTime = (secs) => {
        const m = Math.floor(secs / 60).toString().padStart(2, "0");
        const s = (secs % 60).toString().padStart(2, "0");
        return `${m}:${s}`;
    };

    const handleChange = (idx, val) => {
        // Accept only digits
        const digit = val.replace(/\D/g, "").slice(-1);
        const next = [...digits];
        next[idx] = digit;
        setDigits(next);
        setError("");
        // Auto-advance
        if (digit && idx < OTP_LENGTH - 1) {
            inputRefs.current[idx + 1]?.focus();
        }
        // Auto-submit when all filled
        if (digit && idx === OTP_LENGTH - 1) {
            const otp = [...next].join("");
            if (otp.length === OTP_LENGTH) submit(otp);
        }
    };

    const handleKeyDown = (idx, e) => {
        if (e.key === "Backspace" && !digits[idx] && idx > 0) {
            inputRefs.current[idx - 1]?.focus();
        }
        if (e.key === "ArrowLeft" && idx > 0) inputRefs.current[idx - 1]?.focus();
        if (e.key === "ArrowRight" && idx < OTP_LENGTH - 1) inputRefs.current[idx + 1]?.focus();
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
        if (!pasted) return;
        const next = Array(OTP_LENGTH).fill("");
        pasted.split("").forEach((ch, i) => { next[i] = ch; });
        setDigits(next);
        setError("");
        const focusIdx = Math.min(pasted.length, OTP_LENGTH - 1);
        inputRefs.current[focusIdx]?.focus();
        if (pasted.length === OTP_LENGTH) submit(pasted);
    };

    const submit = useCallback(async (otp) => {
        if (loading) return;
        setLoading(true);
        setError("");
        try {
            await verifyEmail(email, otp);
            toast.success(t("verifyEmail.toasts.verified"));
            nav("/app");
        } catch (err) {
            const msg = err?.response?.data?.detail || t("verifyEmail.toasts.defaultFail");
            setError(msg);
            // Clear inputs on fatal errors
            if (msg.includes("expired") || msg.includes("Too many")) {
                setDigits(Array(OTP_LENGTH).fill(""));
                inputRefs.current[0]?.focus();
            }
        } finally {
            setLoading(false);
        }
    }, [loading, verifyEmail, email, nav, t]);

    const handleSubmitBtn = () => {
        const otp = digits.join("");
        if (otp.length < OTP_LENGTH) {
            setError(t("verifyEmail.errComplete6Digits"));
            return;
        }
        submit(otp);
    };

    const handleResend = async () => {
        if (cooldown > 0 || resending) return;
        setResending(true);
        try {
            await authApi.resendVerification(email);
            toast.success(t("verifyEmail.toasts.newCodeSent"));
            setDigits(Array(OTP_LENGTH).fill(""));
            setTimeLeft(EXPIRY_MINUTES * 60);
            setCooldown(RESEND_COOLDOWN);
            setError("");
            inputRefs.current[0]?.focus();
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("verifyEmail.toasts.resendFail"));
        } finally {
            setResending(false);
        }
    };

    const isExpired = timeLeft <= 0;
    const otpFilled = digits.every((d) => d !== "");

    return (
        <div className="min-h-screen relative grain flex flex-col" data-testid="verify-email-page">
            <div className="p-6 md:p-10">
                <Link to="/auth" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-[color:var(--gold)]">
                    <ArrowLeft size={14} /> {t("verifyEmail.backToSignIn")}
                </Link>
            </div>

            <div className="flex-1 flex items-center justify-center px-6 pb-16">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="w-full max-w-md"
                >
                    {/* Header */}
                    <div className="text-center mb-10">
                        <div className="w-16 h-16 mx-auto mb-6 rounded-2xl grid place-items-center"
                            style={{ background: "rgba(212,175,55,0.1)", border: "1px solid rgba(212,175,55,0.3)" }}>
                            <PassportLogo size={40} />
                        </div>
                        <h1 className="font-serif text-4xl md:text-5xl text-white leading-none tracking-tighter">{t("verifyEmail.title")}</h1>
                        <div className="flex items-center justify-center gap-2 mt-3 text-sm text-white/50">
                            <EnvelopeSimple size={14} />
                            <span>{email || t("verifyEmail.emailFallback")}</span>
                        </div>
                    </div>

                    {/* Card */}
                    <div className="rounded-3xl p-8" style={{ background: "var(--navy-deep)", border: "1px solid rgba(255,255,255,0.06)" }}>
                        <p className="text-center text-sm text-white/60 mb-6 leading-relaxed">
                            {t("verifyEmail.subtitle")}
                        </p>

                        {/* Expiry timer */}
                        <div className="text-center mb-6">
                            <span className="text-[11px] tracking-[0.2em] uppercase" style={{ color: isExpired ? "#ef4444" : "rgba(255,255,255,0.35)" }}>
                                {isExpired ? t("verifyEmail.codeExpired") : t("verifyEmail.expiresIn", { time: formatTime(timeLeft) })}
                            </span>
                        </div>

                        {/* OTP inputs */}
                        <div className="flex justify-center gap-3 mb-6" onPaste={handlePaste}>
                            {digits.map((d, idx) => (
                                <input
                                    key={idx}
                                    ref={(el) => (inputRefs.current[idx] = el)}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={1}
                                    value={d}
                                    onChange={(e) => handleChange(idx, e.target.value)}
                                    onKeyDown={(e) => handleKeyDown(idx, e)}
                                    disabled={loading || isExpired}
                                    data-testid={`otp-digit-${idx}`}
                                    style={{
                                        width: 48,
                                        height: 56,
                                        textAlign: "center",
                                        fontSize: 22,
                                        fontFamily: "Georgia, serif",
                                        fontWeight: 700,
                                        color: "white",
                                        background: d ? "rgba(212,175,55,0.08)" : "rgba(0,0,0,0.3)",
                                        border: `1.5px solid ${error ? "rgba(239,68,68,0.6)" : d ? "rgba(212,175,55,0.5)" : "rgba(255,255,255,0.1)"}`,
                                        borderRadius: 12,
                                        outline: "none",
                                        transition: "all 0.15s ease",
                                        caretColor: "var(--gold)",
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = "var(--gold)"}
                                    onBlur={(e) => e.target.style.borderColor = error ? "rgba(239,68,68,0.6)" : d ? "rgba(212,175,55,0.5)" : "rgba(255,255,255,0.1)"}
                                />
                            ))}
                        </div>

                        {/* Error message */}
                        <AnimatePresence>
                            {error && (
                                <motion.p
                                    initial={{ opacity: 0, y: -6 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0 }}
                                    className="text-center text-sm mb-4"
                                    style={{ color: "#f87171" }}
                                    data-testid="otp-error"
                                >
                                    {error}
                                </motion.p>
                            )}
                        </AnimatePresence>

                        {/* Submit button */}
                        <button
                            onClick={handleSubmitBtn}
                            disabled={loading || isExpired || !otpFilled}
                            className="btn-gold w-full justify-center"
                            data-testid="otp-submit"
                            style={{ opacity: (loading || isExpired || !otpFilled) ? 0.5 : 1 }}
                        >
                            {loading
                                ? t("verifyEmail.verifying")
                                : <><CheckCircle size={16} weight="fill" /> {t("verifyEmail.verifyBtn")}</>
                            }
                        </button>

                        {/* Resend section */}
                        <div className="mt-6 text-center">
                            <p className="text-xs text-white/40 mb-2">{t("verifyEmail.didntReceive")}</p>
                            <button
                                onClick={handleResend}
                                disabled={cooldown > 0 || resending || loading}
                                className="inline-flex items-center gap-1.5 text-sm transition-colors"
                                style={{ color: (cooldown > 0 || resending) ? "rgba(255,255,255,0.3)" : "var(--gold)", cursor: (cooldown > 0 || resending) ? "not-allowed" : "pointer" }}
                                data-testid="otp-resend"
                            >
                                <ArrowClockwise size={13} weight={resending ? "regular" : "bold"} />
                                {resending ? t("verifyEmail.sending") : cooldown > 0 ? t("verifyEmail.resendIn", { cooldown }) : t("verifyEmail.resendCode")}
                            </button>
                        </div>

                        {/* Check spam note */}
                        <p className="text-center text-[11px] text-white/25 mt-4 leading-relaxed">
                            {t("verifyEmail.spamNote")}
                        </p>
                    </div>

                    <p className="text-center text-xs text-white/40 mt-6">
                        {t("verifyEmail.wrongEmail")}{" "}
                        <Link to="/auth?mode=signup" className="text-white/60 hover:text-[color:var(--gold)] transition-colors">
                            {t("verifyEmail.signUpAgain")}
                        </Link>
                    </p>
                </motion.div>
            </div>
        </div>
    );
}
